import React, { useEffect, useMemo, useState } from 'react';
import useApi from '../api';
import { Checkbox } from '@/components/ui/checkbox';
import { OrgUser, orgUserLabel } from './servicesShared';

/** Loads the organization's dashboard users once (GET /users/). */
export function useOrgUsers() {
    const { get } = useApi();
    const [users, setUsers] = useState<OrgUser[]>([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        get('/users/')
            .then((response) => {
                if (!cancelled) setUsers(Array.isArray(response.data) ? response.data : []);
            })
            .catch(() => {
                if (!cancelled) setUsers([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const byId = useMemo(() => new Map(users.map((user) => [user.id, user])), [users]);
    return { users, byId, loading };
}

/**
 * Stewards ("Your team" in the UI): the dashboard users who answer a
 * job's questions and approvals. Multi-select over the org's users.
 */
export const StewardsSelect: React.FC<{
    value: string[];
    onChange: (next: string[]) => void;
    users?: OrgUser[];
    loading?: boolean;
    emptyHint?: string;
}> = ({ value, onChange, users: provided, loading: providedLoading, emptyHint }) => {
    const fallback = useOrgUsers();
    const users = provided ?? fallback.users;
    const loading = providedLoading ?? fallback.loading;
    const selected = new Set(value);

    if (loading) return <p className="text-xs text-muted-foreground">Loading your team…</p>;
    if (!users.length) return <p className="text-xs text-muted-foreground">{emptyHint || 'No dashboard users found.'}</p>;

    return (
        <div className="grid sm:grid-cols-2 gap-1.5">
            {users.map((user) => (
                <label key={user.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                        checked={selected.has(user.id)}
                        onCheckedChange={(checked) =>
                            onChange(checked === true ? Array.from(new Set([...value, user.id])) : value.filter((id) => id !== user.id))
                        }
                    />
                    <span className="truncate" title={user.email || undefined}>
                        {orgUserLabel(user)}
                    </span>
                </label>
            ))}
        </div>
    );
};

export const StewardNames: React.FC<{ ids: string[]; byId: Map<string, OrgUser> }> = ({ ids, byId }) => {
    if (!ids.length) return <span className="text-muted-foreground">No one yet</span>;
    return <>{ids.map((id) => orgUserLabel(byId.get(id), 'Former user')).join(', ')}</>;
};

export default StewardsSelect;
